import { useState, type CSSProperties, type FormEvent } from "react";
import { useGame } from "@game/game-context";
import type { Me } from "@game/types";
import type { ApiError } from "@game/services";

type Mode = "login" | "register" | "forgot";

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "linear-gradient(135deg, #0b0d12 0%, #1a1538 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 5000,
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
};

const cardStyle: CSSProperties = {
  width: 380,
  background: "#0f1218",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 12,
  padding: 28,
  color: "#e6e8ee",
  boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
};

const inputStyle: CSSProperties = {
  width: "100%",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 6,
  color: "#e6e8ee",
  padding: "8px 10px",
  fontSize: 14,
  fontFamily: "inherit",
  marginTop: 4,
};

const labelStyle: CSSProperties = {
  fontSize: 12,
  color: "rgba(230,232,238,0.7)",
};

const primaryBtn: CSSProperties = {
  width: "100%",
  background: "linear-gradient(135deg, #4cb1ff, #a855f7)",
  border: "none",
  color: "#fff",
  padding: "10px",
  borderRadius: 6,
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 14,
  marginTop: 18,
};

const linkBtn: CSSProperties = {
  background: "none",
  border: "none",
  color: "#7dd3fc",
  fontSize: 12,
  cursor: "pointer",
  padding: 0,
};

const ERR_MAP: Record<string, string> = {
  invalid_credentials: "Email or password incorrect.",
  banned: "Account suspended.",
  email_taken: "That email is already registered.",
  name_taken: "That character name is taken.",
  invalid: "Some fields are invalid.",
  invalid_refresh_token: "Session expired. Please log in again.",
};

export function AuthScene() {
  const game = useGame();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [characterName, setCharacterName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      if (mode === "login") {
        await game.client.auth.login(email.trim(), password);
        await postAuth();
      } else if (mode === "register") {
        await game.client.auth.register(email.trim(), password, characterName.trim());
        await postAuth();
      } else {
        await game.client.auth.forgotPassword(email.trim());
        setInfo("If that email is registered, a reset link has been sent.");
      }
    } catch (err) {
      const code = (err as ApiError).code ?? "unknown";
      setError(ERR_MAP[code] ?? `Login failed (${code})`);
    } finally {
      setSubmitting(false);
    }
  };

  const postAuth = async () => {
    // After login/register, jump to boot (which will handle onboarding gate).
    try {
      const me = await game.client.api.get<Me>("/me");
      if (!me.onboardingComplete) {
        game.setStage({ kind: "onboarding", characterId: me.id });
        return;
      }
      game.setStage({ kind: "boot" });
    } catch {
      game.setStage({ kind: "boot" });
    }
  };

  return (
    <div style={overlayStyle}>
      <form onSubmit={submit} style={cardStyle}>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: 22 }}>
          {mode === "login" && "Welcome back"}
          {mode === "register" && "Create account"}
          {mode === "forgot" && "Reset password"}
        </h2>
        <div style={{ marginTop: 4, opacity: 0.65, fontSize: 13 }}>
          {mode === "login" && "Sign in to continue your journey."}
          {mode === "register" && "Pick a tamer name. You can't change it later."}
          {mode === "forgot" && "Enter your email to receive a reset link."}
        </div>

        <div style={{ marginTop: 18 }}>
          <label style={labelStyle} htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            disabled={submitting}
          />
        </div>

        {mode !== "forgot" && (
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle} htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              minLength={8}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              disabled={submitting}
            />
          </div>
        )}

        {mode === "register" && (
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle} htmlFor="characterName">Character name</label>
            <input
              id="characterName"
              type="text"
              autoComplete="username"
              minLength={3}
              maxLength={20}
              required
              value={characterName}
              onChange={(e) => setCharacterName(e.target.value)}
              style={inputStyle}
              disabled={submitting}
            />
          </div>
        )}

        {error && (
          <div style={{ marginTop: 14, color: "#ef4444", fontSize: 12 }}>{error}</div>
        )}
        {info && (
          <div style={{ marginTop: 14, color: "#86efac", fontSize: 12 }}>{info}</div>
        )}

        <button type="submit" disabled={submitting} style={primaryBtn}>
          {submitting
            ? "…"
            : mode === "login"
              ? "Sign in"
              : mode === "register"
                ? "Create account"
                : "Send reset link"}
        </button>

        <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {mode === "login" && (
            <>
              <button type="button" style={linkBtn} onClick={() => setMode("register")}>
                Create an account
              </button>
              <button type="button" style={linkBtn} onClick={() => setMode("forgot")}>
                Forgot password?
              </button>
            </>
          )}
          {mode === "register" && (
            <button type="button" style={linkBtn} onClick={() => setMode("login")}>
              ← Back to sign in
            </button>
          )}
          {mode === "forgot" && (
            <button type="button" style={linkBtn} onClick={() => setMode("login")}>
              ← Back to sign in
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
